// Reports — tenant-aware Meta performance and campaign controls.
// Supabase sessions use company-scoped SaaS APIs; standalone sessions retain
// the original brand-slug Meta route branch.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { IconChevron, IconInfo, IconPause, IconPlay, IconRefresh } from '../components/icons.jsx';
import { Card, Pill } from '../components/primitives.jsx';
import Beacon from '../components/Beacon.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import {
  ConfirmationDialog,
  useConfirmationDialog,
} from '../components/ConfirmationDialog.jsx';
import { PALETTE } from '../palette.js';

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'last_7d', label: 'Last 7 days' },
  { value: 'last_30d', label: 'Last 30 days' },
  { value: 'maximum', label: 'Lifetime' },
];

const reportsResponsiveStyles = `
  .reports-page { min-width: 0; }
  .reports-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .reports-row-head,
  .reports-campaign-row { display: grid; grid-template-columns: minmax(0, 2fr) 124px repeat(4, minmax(82px, .7fr)) 92px; }
  .reports-child-row { display: grid; grid-template-columns: minmax(0, 2fr) 124px repeat(4, minmax(82px, .7fr)) 92px; }
  .reports-mobile-metrics { display: none; }
  @media (max-width: 920px) {
    .reports-page { padding: 24px 16px 48px !important; }
    .reports-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .reports-row-head { display: none; }
    .reports-campaign-row,
    .reports-child-row { grid-template-columns: minmax(0, 1fr) auto; gap: 12px; }
    .reports-desktop-metric { display: none !important; }
    .reports-mobile-metrics { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-column: 1 / -1; gap: 10px; }
    .reports-row-actions { grid-column: 2; grid-row: 1; }
    .reports-child-indent { padding-left: 14px !important; }
  }
  @media (max-width: 520px) {
    .reports-summary { grid-template-columns: minmax(0, 1fr); }
    .reports-controls { grid-template-columns: minmax(0, 1fr) !important; }
    .reports-campaign-row,
    .reports-child-row { grid-template-columns: minmax(0, 1fr); }
    .reports-row-actions { grid-column: 1; grid-row: auto; justify-content: flex-start !important; }
  }
`;

function activeCompanyId(session) {
  const memberships = session?.user?.memberships || [];
  return session?.user?.activeCompany?.id || memberships[0]?.companyId || '';
}

function activeTenantRole(session, companyId = activeCompanyId(session)) {
  return session?.user?.role
    || session?.user?.memberships?.find((membership) => membership.companyId === companyId)?.role
    || null;
}

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

export function canManageSaasReports(session, companyId = activeCompanyId(session)) {
  return ['owner', 'admin', 'marketer'].includes(String(activeTenantRole(session, companyId) || '').toLowerCase());
}

export function safeMetaReportError(error, fallback = 'Meta reports could not be loaded. Try again.') {
  const code = String(error?.code || '').toLowerCase();
  const messages = {
    meta_connection_required: 'Connect Meta Ads in Company settings to view reporting.',
    meta_connection_reauth_required: 'Reconnect Meta Ads in Company settings to restore reporting.',
    meta_connection_health_check_required: 'Verify the Meta Ads connection in Company settings before loading reports.',
    meta_connection_incomplete: 'Reconnect Meta Ads to finish selecting the reporting account.',
    meta_scope_insufficient: 'Reconnect Meta Ads with reporting access before loading campaign performance.',
    meta_rate_limited: 'Meta is limiting report requests right now. Wait a moment and refresh.',
    meta_provider_unavailable: 'Meta reporting is temporarily unavailable. Try again shortly.',
    meta_object_not_found: 'That Meta campaign item is no longer available in this company account.',
    meta_status_audit_unavailable: 'Meta status controls are temporarily unavailable. Try again shortly.',
    meta_status_audit_confirmation_failed: 'Meta received the status request, but confirmation is incomplete. Refresh before trying again.',
  };
  if (messages[code]) return messages[code];
  if (Number(error?.status) === 403) return 'Your company role cannot manage Meta campaign status.';
  return fallback;
}

function reportStorageKey(isSaasSession, companyId) {
  return isSaasSession ? `reports:lastBrand:${companyId}` : 'campaigns:lastBrand';
}

export default function Reports({ navigate, session }) {
  const companyId = activeCompanyId(session);
  const isSaasSession = session?.user?.auth?.type === 'supabase';
  const canManage = !isSaasSession || canManageSaasReports(session, companyId);
  const [brands, setBrands] = useState(null);
  const [brandKey, setBrandKey] = useState('');
  const [preset, setPreset] = useState('last_7d');
  const [campaigns, setCampaigns] = useState(null);
  const [summary, setSummary] = useState(null);
  const [connection, setConnection] = useState(null);
  const [partialData, setPartialData] = useState([]);
  const [details, setDetails] = useState({});
  const [openId, setOpenId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [actionKey, setActionKey] = useState('');
  const [error, setError] = useState('');
  const scopeKey = `${isSaasSession ? 'saas' : 'legacy'}:${companyId}:${brandKey}:${preset}`;
  const activeScopeRef = useRef(scopeKey);
  const listRequestRef = useRef(0);
  const detailRequestRef = useRef(0);
  const campaignsRef = useRef(campaigns);
  const detailsRef = useRef(details);
  const actionKeyRef = useRef(actionKey);
  const canManageRef = useRef(canManage);
  const confirmationDialog = useConfirmationDialog();
  activeScopeRef.current = scopeKey;
  campaignsRef.current = campaigns;
  detailsRef.current = details;
  actionKeyRef.current = actionKey;
  canManageRef.current = canManage;

  useEffect(() => {
    let active = true;
    setBrands(null);
    setBrandKey('');
    setCampaigns(null);
    setSummary(null);
    setConnection(null);
    setPartialData([]);
    setDetails({});
    setOpenId(null);
    setError('');
    const request = isSaasSession
      ? api.get('/api/saas/brands', tenantHeaders(companyId)).then((response) => response.brands || [])
      : api.get('/api/clients').then((response) => response.clients || []);
    request.then((list) => {
      if (!active) return;
      setBrands(list);
      const saved = localStorage.getItem(reportStorageKey(isSaasSession, companyId)) || '';
      const keyFor = (brand) => isSaasSession ? brand.id : brand.slug;
      const selected = list.some((brand) => keyFor(brand) === saved) ? saved : (list[0] ? keyFor(list[0]) : '');
      setBrandKey(selected);
    }).catch((requestError) => {
      if (!active) return;
      setBrands([]);
      setError(safeMetaReportError(requestError, 'Company brands could not be loaded. Try again.'));
    });
    return () => { active = false; };
  }, [isSaasSession, companyId]);

  useEffect(() => {
    if (brandKey) localStorage.setItem(reportStorageKey(isSaasSession, companyId), brandKey);
  }, [brandKey, isSaasSession, companyId]);

  const reload = useCallback(async () => {
    const requestScope = scopeKey;
    const requestId = ++listRequestRef.current;
    detailRequestRef.current += 1;
    const requestIsCurrent = () => (
      activeScopeRef.current === requestScope && listRequestRef.current === requestId
    );
    if (!brandKey) {
      setCampaigns(null);
      setSummary(null);
      setConnection(null);
      setRefreshing(false);
      return;
    }
    setRefreshing(true);
    setError('');
    setPartialData([]);
    try {
      if (isSaasSession) {
        const response = await api.get(
          `/api/saas/meta/reports?brandId=${encodeURIComponent(brandKey)}&datePreset=${encodeURIComponent(preset)}`,
          tenantHeaders(companyId),
        );
        if (!requestIsCurrent()) return;
        setCampaigns(response.campaigns || []);
        setSummary(response.summary || null);
        setConnection(response.connection || null);
        setPartialData(response.partialData || []);
      } else {
        const response = await api.get(`/api/meta/campaigns/${encodeURIComponent(brandKey)}?datePreset=${encodeURIComponent(preset)}`);
        if (!requestIsCurrent()) return;
        setCampaigns(response.campaigns || []);
        setSummary(null);
        setConnection(null);
        setPartialData(response.insightsError ? [{ code: 'meta_insights_unavailable' }] : []);
      }
      setDetails({});
      setOpenId(null);
    } catch (requestError) {
      if (!requestIsCurrent()) return;
      setCampaigns([]);
      setSummary(null);
      setConnection(null);
      setError(safeMetaReportError(requestError));
    } finally {
      if (requestIsCurrent()) setRefreshing(false);
    }
  }, [brandKey, preset, isSaasSession, companyId, scopeKey]);

  useEffect(() => { reload(); }, [reload]);

  async function toggleDrill(campaignId) {
    if (openId === campaignId) {
      detailRequestRef.current += 1;
      setOpenId(null);
      return;
    }
    const requestScope = scopeKey;
    const requestId = ++detailRequestRef.current;
    const requestIsCurrent = () => (
      activeScopeRef.current === requestScope && detailRequestRef.current === requestId
    );
    setOpenId(campaignId);
    if (details[campaignId]) return;
    setDetails((current) => ({ ...current, [campaignId]: { loading: true } }));
    try {
      const response = isSaasSession
        ? await api.get(
          `/api/saas/meta/reports/${encodeURIComponent(campaignId)}?brandId=${encodeURIComponent(brandKey)}&datePreset=${encodeURIComponent(preset)}`,
          tenantHeaders(companyId),
        )
        : await api.get(`/api/meta/campaigns/${encodeURIComponent(brandKey)}/${encodeURIComponent(campaignId)}?datePreset=${encodeURIComponent(preset)}`);
      if (!requestIsCurrent()) return;
      setDetails((current) => ({
        ...current,
        [campaignId]: {
          loading: false,
          adsets: response.adsets || [],
          partialData: response.partialData || [],
        },
      }));
    } catch (requestError) {
      if (!requestIsCurrent()) return;
      setDetails((current) => ({
        ...current,
        [campaignId]: { loading: false, error: safeMetaReportError(requestError, 'Campaign details could not be loaded.') },
      }));
    }
  }

  async function updateStatus(kind, targetId, campaignId, currentStatus) {
    const nextStatus = String(currentStatus || '').toUpperCase() === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    const key = `${kind}:${targetId}`;
    const requestScope = scopeKey;
    if (actionKeyRef.current) return;
    if (nextStatus === 'ACTIVE') {
      const targetLabel = kind === 'adset' ? 'ad set' : kind;
      const approved = await confirmationDialog.request({
        eyebrow: 'Meta delivery',
        title: `Resume this Meta ${targetLabel}?`,
        description: 'This changes the selected Meta item to Active. Delivery and spending may begin from the connected company account once Meta permits delivery.',
        confirmLabel: 'Resume in Meta',
        cancelLabel: 'Keep paused',
        tone: 'warning',
      });
      if (!approved) return;
      let currentTarget = null;
      if (kind === 'campaign') {
        currentTarget = (campaignsRef.current || []).find((campaign) => (
          campaign.id === targetId && campaign.id === campaignId
        ));
      } else {
        const adsets = detailsRef.current[campaignId]?.adsets || [];
        if (kind === 'adset') currentTarget = adsets.find((adset) => adset.id === targetId);
        if (kind === 'ad') {
          currentTarget = adsets.flatMap((adset) => adset.ads || []).find((ad) => ad.id === targetId);
        }
      }
      const targetStatus = String(currentTarget?.effectiveStatus || currentTarget?.status || '').toUpperCase();
      if (
        activeScopeRef.current !== requestScope
        || !canManageRef.current
        || !currentTarget
        || targetStatus !== String(currentStatus || '').toUpperCase()
        || actionKeyRef.current
      ) return;
    }
    actionKeyRef.current = key;
    setActionKey(key);
    setError('');
    try {
      if (isSaasSession) {
        await api.patch(
          `/api/saas/meta/reports/${encodeURIComponent(kind)}/${encodeURIComponent(targetId)}/status`,
          { brandId: brandKey, campaignId, status: nextStatus },
          tenantHeaders(companyId),
        );
      } else {
        await api.post(`/api/meta/${kind}/${encodeURIComponent(brandKey)}/${encodeURIComponent(targetId)}/status`, { status: nextStatus });
      }
      if (activeScopeRef.current !== requestScope) return;
      await reload();
    } catch (requestError) {
      if (activeScopeRef.current === requestScope) {
        setError(safeMetaReportError(requestError, 'Meta campaign status could not be updated.'));
      }
    } finally {
      actionKeyRef.current = '';
      setActionKey('');
    }
  }

  const calculatedSummary = useMemo(() => {
    if (summary) return summary;
    const result = (campaigns || []).reduce((totals, campaign) => {
      totals.spend += Number(campaign.metrics?.spend || 0);
      totals.leads += Number(campaign.metrics?.leads || 0);
      if (campaign.effectiveStatus === 'ACTIVE') totals.active += 1;
      return totals;
    }, { spend: 0, leads: 0, active: 0 });
    return { ...result, costPerLead: result.leads > 0 ? result.spend / result.leads : null };
  }, [campaigns, summary]);

  if (isSaasSession && (brands === null || (brandKey && campaigns === null))) {
    return <ProtectedPageLoader title="Loading Meta reports" body="Checking company access, Meta connection, campaigns, and performance data." slowAfterMs={8000} />;
  }

  return (
    <div className="reports-page" style={{ padding: '32px 40px 64px', maxWidth: 1280, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      <style>{reportsResponsiveStyles}</style>
      <header style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={eyebrowStyle}>Performance</div>
          <h1 style={titleStyle}>Meta reports</h1>
        </div>
        <Pill tone="accent">Company account</Pill>
        <div style={{ flex: 1 }} />
        {connection?.status === 'connected' && <Pill tone="live">Connected</Pill>}
        <button type="button" onClick={reload} disabled={!brandKey || refreshing} style={ghostBtn} title="Refresh Meta reports" aria-busy={refreshing ? 'true' : undefined}>
          {refreshing ? <Beacon size="inline" tone="neutral" delayMs={0} /> : <IconRefresh size={14} />} {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      {error && (
        <Notice tone="danger">
          <span style={{ flex: 1 }}>{error}</span>
          {isSaasSession && (
            <button type="button" onClick={() => navigate('settings')} style={noticeActionBtn}>Open company settings</button>
          )}
        </Notice>
      )}

      {partialData.length > 0 && !error && (
        <Notice tone="warn">
          <IconInfo size={16} />
          Campaign structure loaded, but some performance metrics are temporarily unavailable from Meta.
        </Notice>
      )}

      <Card style={{ padding: 20, marginBottom: 22 }}>
        <div className="reports-controls" style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) minmax(180px, .55fr)', gap: 14 }}>
          <SelectField
            label="Brand"
            value={brandKey}
            onChange={setBrandKey}
            options={(brands || []).map((brand) => ({ value: isSaasSession ? brand.id : brand.slug, label: brand.name }))}
            placeholder={brands == null ? 'Loading' : 'Pick a brand'}
          />
          <SelectField label="Timeframe" value={preset} onChange={setPreset} options={DATE_PRESETS} />
        </div>
        {brandKey && campaigns && (
          <div className="reports-summary" style={{ marginTop: 18, borderTop: `1px solid ${PALETTE.line}` }}>
            <SummaryStat label="Active campaigns" value={fmtInt(calculatedSummary.active)} />
            <SummaryStat label="Spend" value={fmtMoney(calculatedSummary.spend)} />
            <SummaryStat label="Leads" value={fmtInt(calculatedSummary.leads)} />
            <SummaryStat label="Cost per lead" value={calculatedSummary.costPerLead == null ? '—' : fmtMoney(calculatedSummary.costPerLead)} />
          </div>
        )}
      </Card>

      {!brandKey ? (
        <EmptyHint>Pick a brand to view its connected company Meta campaigns.</EmptyHint>
      ) : campaigns == null ? (
        <EmptyHint><Beacon size="control" label="Checking Meta campaigns…" delayMs={0} /></EmptyHint>
      ) : campaigns.length === 0 && !error ? (
        <EmptyHint>No Meta campaigns are available in this company account for the selected brand.</EmptyHint>
      ) : campaigns.length > 0 ? (
        <section aria-label="Meta campaigns">
          <div className="reports-row-head" style={rowHeadStyle}>
            <div>Campaign</div><div>Status</div><div>Spend</div><div>Leads</div><div>Cost / lead</div><div>CPM</div><div>Action</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {campaigns.map((campaign) => (
              <CampaignRow
                key={campaign.id}
                campaign={campaign}
                open={openId === campaign.id}
                detail={details[campaign.id]}
                canManage={canManage}
                actionKey={actionKey}
                onToggle={() => toggleDrill(campaign.id)}
                onStatus={(kind, id, status) => updateStatus(kind, id, campaign.id, status)}
              />
            ))}
          </div>
        </section>
      ) : null}
      <ConfirmationDialog {...confirmationDialog.dialogProps} />
    </div>
  );
}

function CampaignRow({ campaign, open, detail, canManage, actionKey, onToggle, onStatus }) {
  return (
    <article style={campaignCardStyle}>
      <div className="reports-campaign-row" style={campaignRowStyle}>
        <button type="button" onClick={onToggle} style={campaignNameButtonStyle} aria-expanded={open}>
          <span style={{ display: 'inline-flex', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
            <IconChevron size={14} />
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={rowTitleStyle}>{campaign.name}</span>
            <span style={rowSubtitleStyle}>{campaign.objective || 'Meta campaign'} · {fmtMoney(campaign.dailyBudget)}/day</span>
          </span>
        </button>
        <div className="reports-desktop-metric"><StatusChip status={campaign.effectiveStatus || campaign.status} /></div>
        <MetricCell value={fmtMoney(campaign.metrics?.spend)} />
        <MetricCell value={fmtInt(campaign.metrics?.leads)} />
        <MetricCell value={fmtMoneyOrDash(campaign.metrics?.costPerLead)} />
        <MetricCell value={fmtMoneyOrDash(campaign.metrics?.cpm)} />
        <div className="reports-row-actions" style={rowActionsStyle}>
          <StatusAction
            canManage={canManage}
            busy={actionKey === `campaign:${campaign.id}`}
            status={campaign.effectiveStatus || campaign.status}
            onClick={() => onStatus('campaign', campaign.id, campaign.effectiveStatus || campaign.status)}
          />
        </div>
        <MobileMetrics metrics={campaign.metrics} status={campaign.effectiveStatus || campaign.status} />
      </div>
      {open && (
        <div style={detailBandStyle}>
          {!detail || detail.loading ? (
            <DrillNote>Loading ad sets and ads.</DrillNote>
          ) : detail.error ? (
            <DrillNote tone="danger">{detail.error}</DrillNote>
          ) : !detail.adsets?.length ? (
            <DrillNote>No ad sets are available for this campaign.</DrillNote>
          ) : (
            <AdSetList
              adsets={detail.adsets}
              campaignId={campaign.id}
              canManage={canManage}
              actionKey={actionKey}
              onStatus={onStatus}
              partialData={detail.partialData}
            />
          )}
        </div>
      )}
    </article>
  );
}

function AdSetList({ adsets, canManage, actionKey, onStatus, partialData }) {
  return (
    <div>
      {partialData?.length > 0 && <DrillNote tone="warn">Some ad set or ad metrics are temporarily unavailable.</DrillNote>}
      {adsets.map((adSet) => (
        <div key={adSet.id} style={adSetGroupStyle}>
          <ChildRow
            kind="adset"
            item={adSet}
            label="Ad set"
            canManage={canManage}
            actionKey={actionKey}
            onStatus={onStatus}
          />
          {(adSet.ads || []).map((ad) => (
            <ChildRow
              key={ad.id}
              kind="ad"
              item={ad}
              label="Ad"
              canManage={canManage}
              actionKey={actionKey}
              onStatus={onStatus}
              indented
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function ChildRow({ kind, item, label, canManage, actionKey, onStatus, indented = false }) {
  return (
    <div className={`reports-child-row${indented ? ' reports-child-indent' : ''}`} style={{ ...childRowStyle, paddingLeft: indented ? 34 : 14 }}>
      <div style={{ minWidth: 0, display: 'flex', gap: 10, alignItems: 'center' }}>
        {item.thumbnailUrl && <img src={item.thumbnailUrl} alt="" style={thumbnailStyle} />}
        <div style={{ minWidth: 0 }}>
          <div style={childTitleStyle}>{item.name}</div>
          <div style={rowSubtitleStyle}>{label}{item.optimizationGoal ? ` · ${item.optimizationGoal}` : ''}</div>
        </div>
      </div>
      <div className="reports-desktop-metric"><StatusChip status={item.effectiveStatus || item.status} /></div>
      <MetricCell value={fmtMoney(item.metrics?.spend)} />
      <MetricCell value={fmtInt(item.metrics?.leads)} />
      <MetricCell value={fmtMoneyOrDash(item.metrics?.costPerLead)} />
      <MetricCell value={fmtMoneyOrDash(item.metrics?.cpm)} />
      <div className="reports-row-actions" style={rowActionsStyle}>
        <StatusAction
          canManage={canManage}
          busy={actionKey === `${kind}:${item.id}`}
          status={item.effectiveStatus || item.status}
          onClick={() => onStatus(kind, item.id, item.effectiveStatus || item.status)}
        />
      </div>
      <MobileMetrics metrics={item.metrics} status={item.effectiveStatus || item.status} />
    </div>
  );
}

function StatusAction({ canManage, busy, status, onClick }) {
  if (!canManage) return <span style={readOnlyStyle}>View only</span>;
  const active = String(status || '').toUpperCase() === 'ACTIVE';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      style={statusActionStyle}
      title={active ? 'Pause in Meta' : 'Resume in Meta'}
    >
      {active ? <IconPause size={13} /> : <IconPlay size={13} />}
      {busy ? 'Saving' : active ? 'Pause' : 'Resume'}
    </button>
  );
}

function MetricCell({ value }) {
  return <div className="reports-desktop-metric" style={metricCellStyle}>{value}</div>;
}

function MobileMetrics({ metrics = {}, status }) {
  return (
    <div className="reports-mobile-metrics">
      <MobileMetric label="Status" value={<StatusChip status={status} />} />
      <MobileMetric label="Spend" value={fmtMoney(metrics.spend)} />
      <MobileMetric label="Leads" value={fmtInt(metrics.leads)} />
      <MobileMetric label="Cost / lead" value={fmtMoneyOrDash(metrics.costPerLead)} />
    </div>
  );
}

function MobileMetric({ label, value }) {
  return (
    <div style={mobileMetricStyle}>
      <span style={mobileMetricLabelStyle}>{label}</span>
      <span style={mobileMetricValueStyle}>{value}</span>
    </div>
  );
}

function StatusChip({ status }) {
  const normalized = String(status || 'UNKNOWN').toUpperCase();
  const tones = {
    ACTIVE: { bg: PALETTE.successSoft, fg: PALETTE.success, bd: '#CFD9B4' },
    PAUSED: { bg: PALETTE.surfaceAlt, fg: PALETTE.ink3, bd: PALETTE.line },
    DELETED: { bg: PALETTE.dangerSoft, fg: PALETTE.danger, bd: '#E8C9C1' },
    ARCHIVED: { bg: PALETTE.surfaceAlt, fg: PALETTE.ink3, bd: PALETTE.line },
    DISAPPROVED: { bg: PALETTE.dangerSoft, fg: PALETTE.danger, bd: '#E8C9C1' },
    IN_PROCESS: { bg: PALETTE.warnSoft, fg: PALETTE.warn, bd: '#EBDCB2' },
    PENDING_REVIEW: { bg: PALETTE.warnSoft, fg: PALETTE.warn, bd: '#EBDCB2' },
  };
  const tone = tones[normalized] || { bg: PALETTE.surfaceAlt, fg: PALETTE.ink3, bd: PALETTE.line };
  return (
    <span style={{ ...statusChipStyle, background: tone.bg, color: tone.fg, borderColor: tone.bd }}>
      <span style={{ width: 5, height: 5, borderRadius: 999, background: tone.fg }} />
      {normalized.replaceAll('_', ' ')}
    </span>
  );
}

function SelectField({ label, value, onChange, options, placeholder }) {
  return (
    <label>
      <span style={fieldLabelStyle}>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} style={selectStyle}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div style={summaryStatStyle}>
      <div style={summaryLabelStyle}>{label}</div>
      <div style={summaryValueStyle}>{value}</div>
    </div>
  );
}

function Notice({ children, tone }) {
  const colors = tone === 'danger'
    ? { background: PALETTE.dangerSoft, borderColor: '#E8C9C1', color: '#8B2E1A' }
    : { background: PALETTE.warnSoft, borderColor: '#EBDCB2', color: PALETTE.warn };
  return <div style={{ ...noticeStyle, ...colors }}>{children}</div>;
}

function EmptyHint({ children }) {
  return <div style={emptyHintStyle}>{children}</div>;
}

function DrillNote({ children, tone = 'neutral' }) {
  const colors = tone === 'danger'
    ? { background: PALETTE.dangerSoft, borderColor: '#E8C9C1', color: '#8B2E1A' }
    : tone === 'warn'
      ? { background: PALETTE.warnSoft, borderColor: '#EBDCB2', color: PALETTE.warn }
      : { background: PALETTE.surfaceAlt, borderColor: PALETTE.line, color: PALETTE.ink3 };
  return <div style={{ ...drillNoteStyle, ...colors }}>{children}</div>;
}

function fmtMoney(value) {
  if (value == null || Number.isNaN(Number(value))) return '$0.00';
  return `$${Number(value).toFixed(2)}`;
}

function fmtMoneyOrDash(value) {
  return value == null || Number.isNaN(Number(value)) ? '—' : fmtMoney(value);
}

function fmtInt(value) {
  return Number(value || 0).toLocaleString();
}

const titleStyle = { fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', fontSize: 42, fontWeight: 400, lineHeight: 1, margin: '4px 0 0', color: PALETTE.ink, letterSpacing: 0 };
const eyebrowStyle = { fontFamily: 'JetBrains Mono, monospace', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: PALETTE.ink3 };
const fieldLabelStyle = { display: 'block', fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: PALETTE.ink3, marginBottom: 6 };
const selectStyle = { width: '100%', minWidth: 0, padding: '10px 12px', fontSize: 13, fontFamily: 'Inter, sans-serif', color: PALETTE.ink, background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 8, outline: 'none', cursor: 'pointer' };
const ghostBtn = { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 13px', fontSize: 12.5, fontWeight: 500, background: PALETTE.surface, color: PALETTE.ink, border: `1px solid ${PALETTE.line}`, borderRadius: 8, cursor: 'pointer', fontFamily: 'Inter, sans-serif' };
const noticeActionBtn = { padding: 0, background: 'transparent', color: 'inherit', border: 'none', textDecoration: 'underline', cursor: 'pointer', font: 'inherit', fontWeight: 600 };
const noticeStyle = { display: 'flex', alignItems: 'center', gap: 12, border: '1px solid', borderRadius: 8, padding: '12px 14px', fontSize: 13, marginBottom: 18 };
const summaryStatStyle = { minWidth: 0, padding: '16px 14px', borderRight: `1px solid ${PALETTE.line}` };
const summaryLabelStyle = { fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: PALETTE.ink3, fontWeight: 600, marginBottom: 5 };
const summaryValueStyle = { fontFamily: 'Instrument Serif, serif', fontSize: 24, color: PALETTE.ink };
const rowHeadStyle = { alignItems: 'center', gap: 12, padding: '8px 14px', fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: PALETTE.ink3 };
const campaignCardStyle = { background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 8, overflow: 'hidden' };
const campaignRowStyle = { alignItems: 'center', gap: 12, padding: '14px 14px' };
const campaignNameButtonStyle = { minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, padding: 0, border: 'none', background: 'transparent', color: PALETTE.ink, cursor: 'pointer', textAlign: 'left', fontFamily: 'Inter, sans-serif' };
const rowTitleStyle = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14, fontWeight: 600, color: PALETTE.ink };
const childTitleStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12.5, fontWeight: 500, color: PALETTE.ink2 };
const rowSubtitleStyle = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11, color: PALETTE.ink3, marginTop: 3 };
const metricCellStyle = { textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: PALETTE.ink2 };
const rowActionsStyle = { display: 'flex', justifyContent: 'flex-end' };
const statusActionStyle = { minWidth: 82, display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: 6, padding: '6px 9px', borderRadius: 7, border: `1px solid ${PALETTE.line}`, background: PALETTE.surfaceAlt, color: PALETTE.ink2, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'Inter, sans-serif' };
const readOnlyStyle = { fontSize: 10.5, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase' };
const statusChipStyle = { display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%', padding: '2px 8px', border: '1px solid', borderRadius: 999, fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const detailBandStyle = { padding: '0 14px 14px', borderTop: `1px solid ${PALETTE.lineSoft}` };
const adSetGroupStyle = { borderBottom: `1px solid ${PALETTE.lineSoft}` };
const childRowStyle = { alignItems: 'center', gap: 12, paddingTop: 12, paddingBottom: 12 };
const thumbnailStyle = { width: 34, height: 34, objectFit: 'cover', borderRadius: 5, border: `1px solid ${PALETTE.line}`, flex: '0 0 auto' };
const mobileMetricStyle = { minWidth: 0, padding: '8px 10px', background: PALETTE.surfaceAlt, borderRadius: 6 };
const mobileMetricLabelStyle = { display: 'block', fontSize: 9, color: PALETTE.ink3, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 };
const mobileMetricValueStyle = { display: 'block', minWidth: 0, fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5, color: PALETTE.ink2 };
const emptyHintStyle = { padding: '20px 24px', background: PALETTE.surfaceAlt, border: `1px dashed ${PALETTE.line}`, borderRadius: 8, color: PALETTE.ink3, fontSize: 13 };
const drillNoteStyle = { marginTop: 12, padding: '11px 13px', border: '1px solid', borderRadius: 7, fontSize: 12.5 };
